import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import AdminLayout from '../layouts/AdminLayout';
import { useReactToPrint } from 'react-to-print';
import { FaPrint, FaArrowLeft } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

const OrderDetail = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const componentRef = useRef(); // Yazdırılacak alanı seçmek için

    useEffect(() => {
        const fetchOrder = async () => {
            try {
                const res = await axios.get(`/orders/${id}`);
                setOrder(res.data);
            } catch (error) {
                console.error('Sipariş detayı alınamadı');
            }
        };
        fetchOrder();
    }, [id]);

    // Yazdırma Fonksiyonu
    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
        documentTitle: order ? `Siparis_${order.order_number}` : 'Siparis',
    });

    if (!order) return <AdminLayout><div className="p-4">{t('common.loading')}</div></AdminLayout>;

    return (
        <AdminLayout>
            <div className="d-flex justify-content-between align-items-center mb-4 no-print">
                <button onClick={() => navigate(-1)} className="btn btn-secondary">
                    <FaArrowLeft /> {t('order_detail.back')}
                </button>
                <button onClick={handlePrint} className="btn btn-primary">
                    <FaPrint /> {t('order_detail.print')}
                </button>
            </div>

            {/* Yazdırılacak Alan Başlangıcı */}
            <div ref={componentRef} className="card shadow-sm p-5" style={{ minHeight: '800px' }}>
                {/* Başlık */}
                <div className="d-flex justify-content-between border-bottom pb-3 mb-4">
                    <div>
                        <h2 className="text-primary">VegaMat</h2>
                        <p className="text-muted">{t('order_detail.form_title')}</p>
                    </div>
                    <div className="text-end">
                        <h4>{t('order_detail.order_no')}: {order.order_number}</h4>
                        <p>{t('order_detail.date')}: {new Date(order.created_at).toLocaleDateString('tr-TR')}</p>
                        <span className="badge bg-dark text-white fs-6">{t(`my_orders.status_${order.status}`)}</span>
                    </div>
                </div>

                {/* Müşteri ve Teslimat Bilgileri */}
                <div className="row mb-5">
                    <div className="col-6">
                        <h5 className="border-bottom pb-2">{t('order_detail.customer_info')}</h5>
                        <p className="mb-1"><strong>{t('order_detail.company_name')}:</strong> {order.company_name || order.username}</p>
                        <p className="mb-1"><strong>{t('order_detail.email')}:</strong> {order.email}</p>
                        <p className="mb-1"><strong>{t('order_detail.phone')}:</strong> {order.phone}</p>
                    </div>
                    <div className="col-6 text-end">
                        <h5 className="border-bottom pb-2">{t('order_detail.delivery_payment')}</h5>
                        <p className="mb-1">{order.shipping_address}</p>
                        <p className="mb-1"><strong>{t('order_detail.payment')}:</strong> {order.payment_method === 'bank_transfer' ? t('cart.bank_transfer') : t('cart.cash_on_delivery')}</p>
                    </div>
                </div>

                {/* Ürün Tablosu */}
                <table className="table table-bordered border-dark">
                    <thead className="table-light">
                        <tr>
                            <th>{t('order_detail.product_name')}</th>
                            <th>{t('order_detail.variation')}</th>
                            <th className="text-center">{t('order_detail.quantity')}</th>
                            <th className="text-end">{t('order_detail.unit_price')}</th>
                            <th className="text-end">{t('order_detail.total')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {order.items?.map((item, index) => (
                            <tr key={index}>
                                <td>{item.product_name}</td>
                                <td>{item.size} - {item.color}</td>
                                <td className="text-center">{item.quantity}</td>
                                <td className="text-end">{Number(item.unit_price).toLocaleString('tr-TR', { style: 'currency', currency: 'EUR' })}</td>
                                <td className="text-end">{Number(item.total_price).toLocaleString('tr-TR', { style: 'currency', currency: 'EUR' })}</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colSpan="4" className="text-end border-0"><strong>{t('order_detail.subtotal')}:</strong></td>
                            <td className="text-end border-0">{Number(order.total_amount).toLocaleString('tr-TR', { style: 'currency', currency: 'EUR' })}</td>
                        </tr>
                        <tr>
                            <td colSpan="4" className="text-end border-0"><strong>{t('order_detail.shipping')}:</strong></td>
                            <td className="text-end border-0">{Number(order.shipping_cost).toLocaleString('tr-TR', { style: 'currency', currency: 'EUR' })}</td>
                        </tr>
                        <tr className="fs-5">
                            <td colSpan="4" className="text-end border-top border-dark"><strong>{t('order_detail.grand_total')}:</strong></td>
                            <td className="text-end border-top border-dark fw-bold text-success">
                                {Number(order.final_amount).toLocaleString('tr-TR', { style: 'currency', currency: 'EUR' })}
                            </td>
                        </tr>
                    </tfoot>
                </table>

                {/* Notlar */}
                {order.notes && (
                    <div className="mt-4 p-3 bg-light border rounded">
                        <strong>{t('order_detail.order_note')}:</strong>
                        <p className="m-0 fst-italic">{order.notes}</p>
                    </div>
                )}
                
                <div className="mt-auto text-center text-muted small pt-5">
                    <p>{t('order_detail.footer_note')}</p>
                </div>
            </div>
        </AdminLayout>
    );
};

export default OrderDetail;
